import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import ThSearch from '../CmdTable/TableHeadCell/ThSearch';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import CustomerCell from '../CmdTable/TableCells/CustomerCell';
import IconCell from '../CmdTable/TableCells/IconCell';
import DateTimeCell from '../CmdTable/TableCells/DateTimeCell';
import ThSimpleSelect from '../CmdTable/TableHeadCell/ThSimpleSelect';
import ThMultiSelect from '../CmdTable/TableHeadCell/ThMultiSelect';
import ThDateTime from '../CmdTable/TableHeadCell/ThDateTime';
import TextCell from '../CmdTable/TableCells/TextCell';
import { TEmployeeRecord, IEmployeeCustomerCell } from './interfaces';
import {
  EMPLOYEE_TABLE_NAME,
  CHECKBOX_KEY,
  EMPLOYEE_CUSTOMER,
  EMPLOYEE_FIRST_NAME,
  EMPLOYEE_LAST_NAME,
  EMPLOYEE_IS_DRIVER,
  EMPLOYEE_IS_USER,
  EMPLOYEE_PERSONAL_NUMBER,
  EMPLOYEE_WORK_DEPT,
  EMPLOYEE_CREATED_BY,
  EMPLOYEE_CREATED_AT,
  EMPLOYEE_MODIFIED_BY,
  EMPLOYEE_MODIFIED_AT,
  EMPLOYEE_COST_CENTER,
  EMPLOYEE_EMAIL,
  EMPLOYEE_USERNAME,
  EMPLOYEE_STATUS,
  EMPLOYEE_LAST_LOGGED_AT,
  EMPLOYEE_ROLES,
} from './constants';
import { ACTIONS_KEY } from '../CmdTable/constants';
import Actions from './RowActions/Actions';
import IndeterminateCheckbox from '../CmdTable/IndeterminateCheckbox';
import ChipsCellWithModal from './SpecificCell/ChipsCellWithModal';
import { selectedCustomersAtom } from '../../store/recoil/customers';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
} from '../CmdTable/CmdTableState';

interface IUseBleList {
  columns: ColumnDef<TEmployeeRecord>[];
  columnOrder: string[];
  columnVisibility: { [key: string]: boolean };
}

export default function useEmployees(): IUseBleList {
  const { formatMessage: f } = useIntl();
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(EMPLOYEE_TABLE_NAME)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(EMPLOYEE_TABLE_NAME)
  );
  const columnsTable = useRecoilValue<ICmdTableColumn[]>(columnsAtom);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);

  const handleIndeterminateChange = (table) => {
    if (table.getIsSomeRowsSelected()) {
      table.toggleAllRowsSelected(false);
    } else {
      table.toggleAllRowsSelected(!table.getIsAllRowsSelected());
    }
  };

  const handleCheckboxChange = (event, row) => {
    row.getToggleSelectedHandler()(event);
  };

  const handleCells = (data: TEmployeeRecord) => {
    if (Array.isArray(data.roles)) {
      if (data.roles.length > 1) {
        return <ChipsCellWithModal data={data} align="left" />;
      }
      return <TextCell text={data.roles[0] || ''} />;
    }
    return <TextCell text={data.roles || ''} />;
  };

  const getTextCellStatus = (value: string | undefined): string => {
    if (typeof value !== 'string' || !value) {
      return '';
    }

    if (/^\[.*\]/.test(value)) {
      return value;
    }

    return f({ id: `enum.status.user.${value}` });
  };

  const updateTable = React.useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [EMPLOYEE_TABLE_NAME, filterForRequest, sortFilter, selectedCustomers],
    });
  }, [queryClient, filterForRequest, sortFilter, selectedCustomers]);

  const processDateTimeValue = (datetimeValue: string | null): string | null => {
    if (!datetimeValue || datetimeValue === '[N/A]') {
      return null;
    }

    if (datetimeValue.includes(' ') && !datetimeValue.includes('T')) {
      return `${datetimeValue.replace(' ', 'T')}Z`;
    }

    return datetimeValue;
  };

  const columns = React.useMemo<ColumnDef<TEmployeeRecord>[]>(
    () => [
      {
        id: CHECKBOX_KEY,
        header: ({ table }) => (
          <div className="checkbox-container" style={{ margin: 'auto' }}>
            <IndeterminateCheckbox
              id="checkallrecords"
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected()}
              onChange={() => handleIndeterminateChange(table)}
              className="custom-checkbox"
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="checkbox-container" style={{ margin: 'auto' }}>
            <IndeterminateCheckbox
              id="checkOneRecord"
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              indeterminate={row.getIsSomeSelected()}
              onChange={(event) => handleCheckboxChange(event, row)}
              className="custom-checkbox"
            />
          </div>
        ),
        size: 40,
        enablePinning: true,
      },
      {
        accessorKey: EMPLOYEE_CUSTOMER,
        header: (table) => (
          <ThSimpleSelect
            tableName={EMPLOYEE_TABLE_NAME}
            id={EMPLOYEE_CUSTOMER}
            title={f({ id: `employee.field.customerId` })}
            // tooltip={f({ id: `table_list.columns.${PERIOD_LOCK_CUSTOMER_ID_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.customer ? (
            <CustomerCell customer={info.getValue<IEmployeeCustomerCell>()} />
          ) : (
            <TextCell text="" />
          ),
        // minSize: 260,
        minSize: 190,
      },
      {
        accessorKey: EMPLOYEE_FIRST_NAME,
        header: (table) => (
          <ThSearch
            tableName={EMPLOYEE_TABLE_NAME}
            id={EMPLOYEE_FIRST_NAME}
            title={f({ id: `table_list.columns.drivers.name` })}
            // tooltip={f({ id: `table_list.columns.drivers.${EMPLOYEE_FIRST_NAME}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.firstName ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 180,
      },
      {
        accessorKey: EMPLOYEE_LAST_NAME,
        header: (table) => (
          <ThSearch
            tableName={EMPLOYEE_TABLE_NAME}
            id={EMPLOYEE_LAST_NAME}
            title={f({ id: `table_list.columns.${EMPLOYEE_LAST_NAME}` })}
            // tooltip={f({ id: `table_list.columns.bluecoin.${MAC_ADDRESS_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.lastName ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        // minSize: 260,
        minSize: 260,
      },
      {
        accessorKey: EMPLOYEE_IS_DRIVER,
        header: (table) => (
          <ThSimpleSelect
            tableName={EMPLOYEE_TABLE_NAME}
            id={EMPLOYEE_IS_DRIVER}
            title={f({ id: `table_list.columns.${EMPLOYEE_IS_DRIVER}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_TAG_ACTIVE_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.hasDriver ? (
            <IconCell isIcon={info.getValue<boolean>()} icon="cmd-icon-active text-success" />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: EMPLOYEE_IS_USER,
        header: (table) => (
          <ThSimpleSelect
            tableName={EMPLOYEE_TABLE_NAME}
            id={EMPLOYEE_IS_USER}
            title={f({ id: `table_list.columns.${EMPLOYEE_IS_USER}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_TAG_ACTIVE_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.hasUser ? (
            <IconCell isIcon={info.getValue<boolean>()} icon="cmd-icon-active text-success" />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: EMPLOYEE_PERSONAL_NUMBER,
        header: (table) => (
          <ThSearch
            tableName={EMPLOYEE_TABLE_NAME}
            id={EMPLOYEE_PERSONAL_NUMBER}
            title={f({ id: `table_list.columns.${EMPLOYEE_PERSONAL_NUMBER}` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.personalNumber ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: EMPLOYEE_WORK_DEPT,
        header: (table) => (
          <ThMultiSelect
            tableName={EMPLOYEE_TABLE_NAME}
            id={EMPLOYEE_WORK_DEPT}
            title={f({ id: `table_list.columns.${EMPLOYEE_WORK_DEPT}Id` })}
            // tooltip={f({ id: `table_list.columns.bluecoin.${MANUFACTURER_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.workDepartment ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: EMPLOYEE_COST_CENTER,
        header: (table) => (
          <ThMultiSelect
            tableName={EMPLOYEE_TABLE_NAME}
            id={EMPLOYEE_COST_CENTER}
            title={f({ id: `table_list.columns.${EMPLOYEE_COST_CENTER}Id` })}
            // tooltip={f({ id: `table_list.columns.${PERIOD_LOCK_MODIFIED_BY_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.costCenter ? (
            <TextCell text={info.getValue<string>() || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: EMPLOYEE_EMAIL,
        header: (table) => (
          <ThSearch
            tableName={EMPLOYEE_TABLE_NAME}
            id={EMPLOYEE_EMAIL}
            title={f({ id: `table_list.columns.${EMPLOYEE_EMAIL}` })}
            // tooltip={f({ id: `table_list.columns.bluecoin.${MANUFACTURER_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.email ? (
            <TextCell
              text={info.getValue<string>()}
              // tooltipEnabled={true}
              // minWidth={`${info.column.getSize()}px`}
            />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: EMPLOYEE_CREATED_BY,
        header: (table) => (
          <ThMultiSelect
            tableName={EMPLOYEE_TABLE_NAME}
            id={EMPLOYEE_CREATED_BY}
            title={f({ id: `table_list.columns.${EMPLOYEE_CREATED_BY}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_PERIOD_LOCK_DEFINITION_CREATED_BY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.createdBy ? (
            <TextCell text={info.getValue<string>() || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: EMPLOYEE_USERNAME,
        header: (table) => (
          <ThMultiSelect
            tableName={EMPLOYEE_TABLE_NAME}
            id={EMPLOYEE_USERNAME}
            title={f({ id: `table_list.columns.gdprUsers.${EMPLOYEE_USERNAME}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_PERIOD_LOCK_DEFINITION_CREATED_BY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.username ? (
            <TextCell text={info.getValue<string>() || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: EMPLOYEE_STATUS,
        header: (table) => (
          <ThMultiSelect
            tableName={EMPLOYEE_TABLE_NAME}
            id={EMPLOYEE_STATUS}
            title={f({ id: `table_list.columns.employees.${EMPLOYEE_STATUS}` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_PERIOD_LOCK_DEFINITION_CREATED_BY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.status ? (
            <TextCell text={getTextCellStatus(info.getValue<string>()) || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: EMPLOYEE_LAST_LOGGED_AT,
        header: (table) => (
          <ThDateTime
            id={EMPLOYEE_LAST_LOGGED_AT}
            tableName={EMPLOYEE_TABLE_NAME}
            title={f({ id: `table_list.columns.${EMPLOYEE_LAST_LOGGED_AT}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_PERIOD_LOCK_DEFINITION_CREATED_AT}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) => {
          const datetimeValue = info.getValue<string | null>();
          const formattedValue = processDateTimeValue(datetimeValue);

          if (!formattedValue) {
            return <TextCell text="" />;
          }

          return <DateTimeCell datetime={formattedValue} />;
        },
        minSize: 260,
      },
      {
        accessorKey: EMPLOYEE_ROLES,
        header: (table) => (
          <ThMultiSelect
            tableName={EMPLOYEE_TABLE_NAME}
            id={EMPLOYEE_ROLES}
            title={f({ id: `table_list.columns.gdprUsers.role` })}
            // tooltip={f({ id: `table_list.columns.parkingTags.${PARKING_PERIOD_LOCK_DEFINITION_CREATED_BY}.tooltip` })}
            // isSortable={true}
          />
        ),
        cell: (info) => info.row.original && handleCells(info.row.original),
        minSize: 260,
      },
      {
        accessorKey: EMPLOYEE_CREATED_AT,
        header: (table) => (
          <ThDateTime
            id={EMPLOYEE_CREATED_AT}
            tableName={EMPLOYEE_TABLE_NAME}
            title={f({ id: `table_list.columns.${EMPLOYEE_CREATED_AT}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_PERIOD_LOCK_DEFINITION_CREATED_AT}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) => {
          const datetimeValue = info.getValue<string | null>();
          const formattedValue = processDateTimeValue(datetimeValue);

          if (!formattedValue) {
            return <TextCell text="" />;
          }

          return <DateTimeCell datetime={formattedValue} />;
        },
        minSize: 260,
      },
      {
        accessorKey: EMPLOYEE_MODIFIED_BY,
        header: (table) => (
          <ThMultiSelect
            tableName={EMPLOYEE_TABLE_NAME}
            id={EMPLOYEE_MODIFIED_BY}
            title={f({ id: `table_list.columns.${EMPLOYEE_MODIFIED_BY}` })}
            // tooltip={f({ id: `table_list.columns.${PERIOD_LOCK_MODIFIED_BY_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) =>
          info.row.original?.modifiedBy ? (
            <TextCell text={info.getValue<string>() || ''} />
          ) : (
            <TextCell text="" />
          ),
        minSize: 260,
      },
      {
        accessorKey: EMPLOYEE_MODIFIED_AT,
        header: (table) => (
          <ThDateTime
            id={EMPLOYEE_MODIFIED_AT}
            tableName={EMPLOYEE_TABLE_NAME}
            title={f({ id: `table_list.columns.${EMPLOYEE_MODIFIED_AT}` })}
            // tooltip={f({ id: `table_list.columns.${PARKING_PERIOD_LOCK_MODIFIED_AT_KEY}.tooltip` })}
            isSortable={true}
          />
        ),
        cell: (info) => {
          const datetimeValue = info.getValue<string | null>();
          const formattedValue = processDateTimeValue(datetimeValue);

          if (!formattedValue) {
            return <TextCell text="" />;
          }

          return <DateTimeCell datetime={formattedValue} />;
        },
        minSize: 260,
      },

      {
        accessorKey: ACTIONS_KEY,
        header: () => (
          <div className=" th-sticky sticky-col detail">{f({ id: 'table.actionsBtn' })}</div>
        ),
        cell: (info) =>
          info.row.original ? (
            <Actions<TEmployeeRecord> data={info.row.original} updateTable={updateTable} />
          ) : null,
        size: 80, // Adjusted for consistency with header styling
        enablePinning: true,
      },
    ],
    [f, updateTable]
  );

  const columnOrder = React.useMemo(() => {
    const order = [CHECKBOX_KEY, ...columnsTable.map((item) => item.key)];
    order.push(ACTIONS_KEY);
    return order;
  }, [columnsTable]);

  const columnVisibility = React.useMemo(() => {
    return columnsTable.reduce((acc, column) => {
      acc[column.key] = !column.hidden;
      return acc;
    }, {});
  }, [columnsTable]);

  return {
    columns,
    columnOrder,
    columnVisibility,
  };
}
